import { Card, Col, Layout, Row, Divider, Radio } from 'antd';
import { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styles from './AnalyticsPage.module.less';
import api, {
	GameplayHours,
	HealthDto,
	PlayersWithPlaytime,
	ProductStatus,
} from 'api';
import {
	DashboardOutlined,
	UserOutlined,
	LineChartOutlined,
} from '@ant-design/icons';

import ReportValue from 'components/Analytics/ReportValue';
import ReportDateGraph from 'components/Analytics/ReportDateGraph';
import moment from 'moment';
import pluralize from 'pluralize';

type ParamsProps = {
	projectId: string;
};

export function getTimeDisplayFromSeconds(
	t: TFunction,
	seconds: number | undefined,
) {
	var { data, translationKey } = getTimeDisplayValues(seconds);
	return pluralize(t(translationKey), data, true);
}

export function getTimeDisplayValues(seconds: number | undefined) {
	seconds = seconds || 0;

	if (seconds >= 86400) {
		return {
			data: +(seconds / 86400).toFixed(1),
			translationKey: 'analyticsPage.day',
		};
	} else if (seconds >= 360) {
		return {
			data: +(seconds / 3600).toFixed(1),
			translationKey: 'analyticsPage.hour',
		};
	} else {
		return {
			data: +(seconds / 60).toFixed(1),
			translationKey: 'analyticsPage.minute',
		};
	}
}

export function getTimeDisplayFromSecondsWithNoLabel(
	seconds: number | undefined,
) {
	var hours = moment.duration(seconds, 'seconds').asHours();

	if (hours < 1) {
		return '<1';
	}

	return hours.toLocaleString('en-US', {
		maximumFractionDigits: 0,
	});
}

export default function AnalyticsPage() {
	const { t } = useTranslation();
	const { projectId } = useParams<ParamsProps>();
	const [activeUsers, setActiveUsers] = useState<number>();
	const [gameplayHours, setGameplayHours] = useState<string>();
	const [gameplayHoursDisplayKey, setGameplayHoursDisplayKey] =
		useState<string>('analyticsPage.hours');
	const [averageSession, setAverageSession] = useState<string>();
	const [topActiveUsers, setTopActiveUsers] = useState<PlayersWithPlaytime[]>();
	const [totalAllTimeSessions, setTotalAllTimeSessions] = useState<number>();
	const [totalAllTimeHealth, setTotalAllTimeHealth] = useState<HealthDto>();
	const [gameplayHoursMonthly, setGameplayHoursMonthly] =
		useState<GameplayHours[]>();
	const [gameplayHoursYearly, setGameplayHoursYearly] =
		useState<GameplayHours[]>();
	const [productId, setProductId] = useState<string>();
	const [currentGraphToDisplay, setCurrentGraphToDisplay] =
		useState<string>('monthly');
	const [projectStatus, setProjectStatus] = useState<ProductStatus>();
	const [releaseDate, setReleaseDate] = useState(moment());
	useEffect(() => {
		const fetchProductId = async () => {
			const project = await api.getProjectInfo(parseInt(projectId));
			setProjectStatus(project.status);
			setProductId(project.productId);
			setReleaseDate(moment(project.releaseDate));
		};

		fetchProductId().catch(console.error);
	}, []);

	useEffect(() => {
		const fetchActiveUsers = async () => {
			if (productId != undefined) {
				setActiveUsers(await api.activeUsers(productId));
			}
		};

		fetchActiveUsers().catch(console.error);
	}, [productId]);

	useEffect(() => {
		const fetchGameplayHours = async () => {
			if (productId != undefined) {
				var totalSeconds = await api.totalAllTimeSeconds(productId);
				var { data, translationKey } = getTimeDisplayValues(totalSeconds);

				setGameplayHours(data.toString());
				setGameplayHoursDisplayKey(pluralize(translationKey, data));
			}
		};

		fetchGameplayHours().catch(console.error);
	}, [productId]);

	useEffect(() => {
		const fetchAverageSession = async () => {
			if (productId != undefined) {
				const totalSeconds = await api.averageAllTimeSeconds(productId);
				setAverageSession(
					`${(totalSeconds / 60).toLocaleString('en-US', {
						maximumFractionDigits: 0,
					})} ${t('analyticsPage.minutes')}`,
				);
			}
		};

		fetchAverageSession().catch(console.error);
	}, [productId]);

	useEffect(() => {
		const fetchTotalSessions = async () => {
			if (productId != undefined) {
				setTotalAllTimeSessions(await api.totalAllTimeSessions(productId));
			}
		};

		fetchTotalSessions().catch(console.error);
	}, [productId]);

	useEffect(() => {
		const fetchAllTimeHealth = async () => {
			if (productId != undefined) {
				setTotalAllTimeHealth(await api.totalAllTimeHealth(productId));
			}
		};
		fetchAllTimeHealth().catch(console.error);
	}, [productId]);

	useEffect(() => {
		const fetchTopTenPlayersByPlayTime = async () => {
			if (productId != undefined) {
				setTopActiveUsers(await api.topTenPlayersByPlayTime(productId));
			}
		};

		fetchTopTenPlayersByPlayTime().catch(console.error);
	}, [productId]);

	useEffect(() => {
		const fetchGameplayHoursMonthly = async () => {
			if (productId != undefined) {
				setGameplayHoursMonthly(
					convertSecondsToHours(await api.gameplayHoursMonthly(productId)),
				);
			}
		};

		fetchGameplayHoursMonthly().catch(console.error);
	}, [productId]);

	useEffect(() => {
		const fetchGameplayHoursYearly = async () => {
			if (productId != undefined) {
				setGameplayHoursYearly(
					convertSecondsToHours(await api.gameplayHoursYearly(productId)),
				);
			}
		};

		fetchGameplayHoursYearly().catch(console.error);
	}, [productId]);

	function convertSecondsToHours(hours: GameplayHours[]): GameplayHours[] {
		return hours.map<GameplayHours>((hour: GameplayHours) => {
			if (hour.secondsPlayed != undefined) {
				hour.secondsPlayed = Math.trunc(hour.secondsPlayed / 3600);
			}
			return hour;
		});
	}

	const allTimeCardTitle = (
		<>
			<div className={styles.cardHeader}>
				<DashboardOutlined /> {t('analyticsPage.allTime')}
			</div>
		</>
	);

	const timelineCardTitle = (
		<>
			<div className={styles.cardHeader}>
				<LineChartOutlined /> {t('analyticsPage.playTimeLine')}
			</div>
		</>
	);

	const userCardTitle = (
		<>
			<div className={styles.cardHeader}>
				<UserOutlined /> {t('analyticsPage.userData')}
			</div>
		</>
	);

	const releaseBufferElapsed = () => {
		return moment().diff(releaseDate, 'hour') > 24;
	};

	return (
		<>
			<div className={styles.analyticsDetailsContainer}>
				<div className="page-head">
					<div>
						<h2 className="h2">{t('analyticsPage.header')}</h2>
					</div>
				</div>
				{projectStatus != undefined &&
					projectStatus != ProductStatus.Released && (
						<Layout className={styles.contentHolder}>
							<h2>{t('analyticsPage.NotReleased.line1')}</h2>

							<h2>{t('analyticsPage.NotReleased.line2')}</h2>
						</Layout>
					)}
				{projectStatus != undefined &&
					projectStatus == ProductStatus.Released &&
					!releaseBufferElapsed() && (
						<Layout
							className={styles.contentHolder}
							data-testid="released-recently"
						>
							<h2>{t('analyticsPage.ReleasedRecently.line1')}</h2>

							<h2>{t('analyticsPage.ReleasedRecently.line2')}</h2>
						</Layout>
					)}
				{projectStatus != undefined &&
					projectStatus == ProductStatus.Released &&
					releaseBufferElapsed() && (
						<Layout>
							<Row gutter={[20, 32]}>
								<Col xs={6}>
									<Card
										className={styles.userDetailsContainer}
										title={userCardTitle}
									>
										<div>
											<Row gutter={[20, 32]}>
												<Col xs={24}>
													<ReportValue
														testId="activeUserCountLabel"
														data={activeUsers?.toLocaleString('en-US', {
															maximumFractionDigits: 2,
														})}
														translationKey="analyticsPage.activeUsers"
														data-testid={'activeUserCountLabel'}
													/>
												</Col>
												<Divider />
												<Col xs={24}>
													<div
														className={styles.listLabel}
														data-testid="topTenActiveUsersLabel"
													>
														<li>
															<Row>
																<Col xs={16}>
																	{t('analyticsPage.topTenActiveUsers')}
																</Col>{' '}
																<Col xs={8}>{t('analyticsPage.time')}</Col>
															</Row>{' '}
														</li>
													</div>
													<div
														className={styles.list}
														data-testid="topTenActiveUsers"
													>
														{topActiveUsers?.map((player, index) => (
															<li key={index}>
																<Row>
																	<Col xs={16}>{player.username}</Col>{' '}
																	<Col xs={8}>
																		{getTimeDisplayFromSecondsWithNoLabel(
																			player.playtime,
																		)}
																	</Col>
																</Row>{' '}
															</li>
														))}
													</div>
												</Col>
											</Row>
										</div>
									</Card>
								</Col>
								<Col xs={18}>
									<Row gutter={[20, 20]}>
										<Col xs={24}>
											<Card
												className={styles.allTimeDetailsContainer}
												title={allTimeCardTitle}
											>
												<div>
													<Row gutter={[20, 32]}>
														<Col xs={8}>
															<ReportValue
																testId="gameplayHours"
																data={gameplayHours}
																translationKey={
																	gameplayHoursDisplayKey + 'Played'
																}
															/>
														</Col>
														<Col xs={8}>
															<ReportValue
																testId="totalAllTimeSessions"
																data={totalAllTimeSessions?.toLocaleString(
																	'en-US',
																	{
																		maximumFractionDigits: 2,
																	},
																)}
																translationKey="analyticsPage.totalAllTimeSessions"
															/>
														</Col>
														<Col xs={8}>
															<ReportValue
																testId="averageSession"
																data={averageSession}
																translationKey="analyticsPage.averageSession"
															/>
														</Col>
														<Col xs={8}>
															<ReportValue
																testId="totalAllTimeSteps"
																data={totalAllTimeHealth?.steps?.toLocaleString(
																	'en-US',
																	{ maximumFractionDigits: 2 },
																)}
																translationKey="analyticsPage.totalAllTimeSteps"
															/>
														</Col>
														<Col xs={8}>
															<ReportValue
																testId="totalAllTimeMiles"
																data={totalAllTimeHealth?.miles?.toLocaleString(
																	'en-US',
																	{ maximumFractionDigits: 1 },
																)}
																translationKey="analyticsPage.totalAllTimeMiles"
															/>
														</Col>
														<Col xs={8}>
															<ReportValue
																testId="totalAllTimeCalories"
																data={totalAllTimeHealth?.calories?.toLocaleString(
																	'en-US',
																	{ maximumFractionDigits: 2 },
																)}
																translationKey="analyticsPage.totalAllTimeCalories"
															/>
														</Col>
													</Row>
												</div>
											</Card>
										</Col>
										<Col xs={24}>
											<Card
												className={styles.graphDetailsContainer}
												title={timelineCardTitle}
											>
												<Radio.Group
													onChange={(e) => {
														setCurrentGraphToDisplay(e.target.value);
													}}
													value={currentGraphToDisplay}
													style={{ width: '100%', paddingBottom: '16px' }}
												>
													<Radio value={'monthly'}>
														{t('analyticsPage.monthly')}
													</Radio>
													<Radio value={'last30Days'}>
														{t('analyticsPage.daily')}
													</Radio>
												</Radio.Group>
												{currentGraphToDisplay == 'monthly' && (
													<ReportDateGraph
														data={gameplayHoursYearly}
														barDataKey={[
															{
																key: 'secondsPlayed',
																fillColor: '#75C531',
																name: t('analyticsPage.gameplayHours'),
															},
														]}
														dateSpan="monthly"
														name={t('analyticsPage.gameplayHours')}
														axes={[
															{
																labelTag: 'analyticsPage.sessions',
																data: 'numberOfSessions',
															},
															{
																labelTag: 'analyticsPage.players',
																data: 'numberOfPlayers',
															},
														]}
													/>
												)}

												{currentGraphToDisplay == 'last30Days' && (
													<ReportDateGraph
														data={gameplayHoursMonthly}
														barDataKey={[
															{
																key: 'secondsPlayed',
																fillColor: '#75C531',
																name: t('analyticsPage.gameplayHours'),
															},
														]}
														name={t('analyticsPage.gameplayHours')}
														dateSpan="daily"
														axes={[
															{
																labelTag: 'analyticsPage.sessions',
																data: 'numberOfSessions',
															},
															{
																labelTag: 'analyticsPage.players',
																data: 'numberOfPlayers',
															},
														]}
													/>
												)}
											</Card>
										</Col>
									</Row>
								</Col>
							</Row>
						</Layout>
					)}
			</div>
		</>
	);
}
